import { ApiKeys, fetcher } from '@insta-club/api';
import { Page, SettingsResponse, User } from '@insta-club/models';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface ISessionStore {
    session: User | null;
    status: AuthStatus;
    page: Page | null;
    activePageId: number | null;
    hasNoPages: boolean;
    settings: SettingsResponse | null;
    unread_alerts: number;
    clearSession: () => void;
    updateSession: () => Promise<void>;
    setActivePage: (pageId: number | null) => Promise<void>;
    fetchPageData: (pageId: number) => Promise<void>;
    fetchSettings: () => Promise<void>;
    updateAlertCount: (count: number | ((prev: number) => number)) => void;
}

const fetchSessionFromApi = async () => {
    try {
        const response = await fetch('/api/auth/session');
        if (response.ok) {
            const data = await response.json();
            return data
                ? {
                      session: data,
                      status: 'authenticated' as AuthStatus
                  }
                : {
                      session: null,
                      status: 'unauthenticated' as AuthStatus
                  };
        }

        return { session: null, status: 'unauthenticated' as AuthStatus };
    } catch {
        return { session: null, status: 'unauthenticated' as AuthStatus };
    }
};

const fetchPageFromApi = async (pageId: number) => {
    try {
        const response = await fetcher(ApiKeys.GET_PAGE_DETAILS(pageId), 'dashboard', 'GET');
        return response.data ?? null;
    } catch {
        return null;
    }
};

const fetchSettingsFromApi = async () => {
    try {
        const response = await fetcher(ApiKeys.GET_SETTINGS, 'dashboard', 'GET');
        return response ?? null;
    } catch {
        return null;
    }
};

export const useSessionStore = create<ISessionStore>()(
    persist(
        (set, get) => ({
            session: null,
            status: 'loading',
            activePageId: null,
            hasNoPages: false,
            page: null,
            settings: null,
            unread_alerts: 0,
            fetchSettings: async () => {
                const settingsData = await fetchSettingsFromApi();
                set({ settings: settingsData });
            },
            clearSession: () =>
                set({
                    session: null,
                    status: 'unauthenticated',
                    page: null,
                    activePageId: null,
                    hasNoPages: false
                }),

            fetchPageData: async (pageId: number) => {
                const pageData = await fetchPageFromApi(pageId);
                set({ page: pageData });
            },

            setActivePage: async (pageId: number | null) => {
                set({ activePageId: pageId });
                if (pageId) {
                    await get().fetchPageData(pageId);
                } else {
                    set({ page: null });
                }
            },

            updateSession: async () => {
                set({ status: 'loading' });
                const { session, status } = await fetchSessionFromApi();
                if (session) {
                    await get().fetchSettings();
                }
                let activePageId = get().activePageId;
                let hasNoPages = false;

                if (session?.page_ids?.length > 0) {
                    if (!activePageId || !session.page_ids.includes(activePageId)) {
                        activePageId = session.page_ids[0];
                    }
                } else {
                    activePageId = null;
                    hasNoPages = true;
                }

                set({ session, status, activePageId, hasNoPages, unread_alerts: session?.unread_alerts ?? 0 });

                if (activePageId) {
                    await get().fetchPageData(activePageId);
                } else {
                    set({ page: null });
                }
            },
            updateAlertCount: (count: number | ((prev: number) => number)) => {
                set((state) => ({
                    unread_alerts: typeof count === 'function' ? count(state.unread_alerts) : count
                }));
            }
        }),
        {
            name: 'session-storage',
            partialize: (state) => ({
                session: state.session,
                status: state.status,
                activePageId: state.activePageId,
                hasNoPages: state.hasNoPages,
                page: state.page,
                settings: state.settings
            })
        }
    )
);

if (typeof window !== 'undefined') {
    useSessionStore.getState().updateSession();
}

